import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getRecoil } from 'recoil-nexus';
import organizationIdSelector from '../atoms/organizationIdSelector';
import userDataState from '../atoms/userDataAtom';
import { consola } from 'consola';
import { nanoid } from 'nanoid';
import { ReplyToMessage } from '../types/chat';

export interface MessageReaction {
  emoji: string;
  uid: string;
  firstName: string;
  lastName: string;
  reactedOn: Date;
}

export interface ReactionResponse {
  success: boolean;
  reaction?: MessageReaction;
  removedReaction?: MessageReaction;
  error?: string;
  details?: string;
}

export interface ReplyMessageRequest {
  organizationId: string;
  groupId: string;
  message: string;
  replyTo: ReplyToMessage;
  user: {
    uid: string;
    firstName: string;
    lastName: string;
  };
}

export interface ReplyResponse {
  success: boolean;
  messageId?: string;
  error?: string;
  details?: string;
}

/**
 * Add a reaction to a message
 */
export const addReactionToMessage = async (
  messageId: string,
  emoji: string,
  groupId: string
): Promise<ReactionResponse> => {
  try {
    const organizationId = getRecoil(organizationIdSelector);
    const userData = getRecoil(userDataState) as any;

    if (!organizationId || !userData) {
      throw new Error('Missing organization ID or user data');
    }

    if (!groupId || !messageId || !emoji) {
      throw new Error('Missing required data in addReactionToMessage');
    }

    consola.info("+++++ FIRESTORE => MessageReactions: addReactionToMessage", {
      messageId,
      emoji,
      groupId,
    });

    const messageRef = firebase
      .firestore()
      .collection('organizations')
      .doc(organizationId)
      .collection('messageGroups')
      .doc(groupId)
      .collection('messages')
      .doc(messageId);

    const messageDoc = await messageRef.get();
    
    if (!messageDoc.exists) {
      throw new Error('Message not found');
    }

    const messageData = messageDoc.data();
    const reactions = messageData?.reactions || [];

    // Find existing reaction from this user with this emoji
    const existingReactionIndex = reactions.findIndex(
      (r: MessageReaction) => r.emoji === emoji && r.uid === userData.uid
    );

    if (existingReactionIndex >= 0) {
      // User already reacted with this emoji, no change needed
      consola.info("+++++ FIRESTORE => MessageReactions: reaction already exists");
      return { success: true };
    }

    // Add new reaction
    const newReaction: MessageReaction = {
      emoji,
      uid: userData.uid,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      reactedOn: new Date(),
    };

    const updatedReactions = [...reactions, newReaction];

    await messageRef.set(
      {
        reactions: updatedReactions,
      },
      { merge: true }
    );

    consola.info("+++++ FIRESTORE SUCCESS => MessageReactions: addReactionToMessage", {
      reactionsCount: updatedReactions.length,
    });

    return { 
      success: true, 
      reaction: newReaction 
    };
  } catch (error) {
    consola.error("+++++ FIRESTORE ERROR => MessageReactions: addReactionToMessage", error);
    throw error;
  }
};

/**
 * Remove a reaction from a message
 */
export const removeReactionFromMessage = async (
  messageId: string,
  emoji: string,
  groupId: string
): Promise<ReactionResponse> => {
  try {
    const organizationId = getRecoil(organizationIdSelector);
    const userData = getRecoil(userDataState) as any;

    if (!organizationId || !userData) {
      throw new Error('Missing organization ID or user data');
    }

    if (!groupId || !messageId || !emoji) {
      throw new Error('Missing required data in removeReactionFromMessage');
    }

    consola.info("+++++ FIRESTORE => MessageReactions: removeReactionFromMessage", {
      messageId,
      emoji,
      groupId,
    });

    const messageRef = firebase
      .firestore()
      .collection('organizations')
      .doc(organizationId)
      .collection('messageGroups')
      .doc(groupId)
      .collection('messages')
      .doc(messageId);

    const messageDoc = await messageRef.get();
    
    if (!messageDoc.exists) {
      throw new Error('Message not found');
    }

    const messageData = messageDoc.data();
    const reactions = messageData?.reactions || [];

    // Find existing reaction from this user with this emoji
    const existingReactionIndex = reactions.findIndex(
      (r: MessageReaction) => r.emoji === emoji && r.uid === userData.uid
    );

    if (existingReactionIndex === -1) {
      // No reaction to remove
      consola.info("+++++ FIRESTORE => MessageReactions: no reaction to remove");
      return { success: true };
    }

    const removedReaction = reactions[existingReactionIndex];

    // Remove existing reaction
    const updatedReactions = reactions.filter(
      (_: MessageReaction, index: number) => index !== existingReactionIndex
    );

    await messageRef.set(
      {
        reactions: updatedReactions,
      },
      { merge: true }
    );

    consola.info("+++++ FIRESTORE SUCCESS => MessageReactions: removeReactionFromMessage", {
      reactionsCount: updatedReactions.length,
    });

    return { 
      success: true, 
      removedReaction 
    };
  } catch (error) {
    consola.error("+++++ FIRESTORE ERROR => MessageReactions: removeReactionFromMessage", error);
    throw error;
  }
};

/**
 * Toggle a reaction on a message (add if doesn't exist, remove if exists)
 */
export const toggleMessageReaction = async (
  messageId: string,
  emoji: string,
  groupId: string
): Promise<ReactionResponse> => {
  try {
    const organizationId = getRecoil(organizationIdSelector);
    const userData = getRecoil(userDataState) as any;

    if (!organizationId || !userData) {
      throw new Error('Missing organization ID or user data');
    }

    if (!groupId || !messageId || !emoji) {
      throw new Error('Missing required data in toggleMessageReaction');
    }

    consola.info("+++++ FIRESTORE => MessageReactions: toggleMessageReaction", {
      messageId,
      emoji,
      groupId,
    });

    const messageRef = firebase
      .firestore()
      .collection('organizations')
      .doc(organizationId)
      .collection('messageGroups')
      .doc(groupId)
      .collection('messages')
      .doc(messageId);

    const messageDoc = await messageRef.get();
    
    if (!messageDoc.exists) {
      throw new Error('Message not found');
    }

    const messageData = messageDoc.data();
    const reactions = messageData?.reactions || [];

    // Find existing reaction from this user with this emoji
    const existingReactionIndex = reactions.findIndex(
      (r: MessageReaction) => r.emoji === emoji && r.uid === userData.uid
    );

    let updatedReactions: MessageReaction[];
    let isAdding: boolean;
    let reaction: MessageReaction | undefined;
    let removedReaction: MessageReaction | undefined;

    if (existingReactionIndex >= 0) {
      // Remove existing reaction
      removedReaction = reactions[existingReactionIndex];
      updatedReactions = reactions.filter(
        (_: MessageReaction, index: number) => index !== existingReactionIndex
      );
      isAdding = false;
    } else {
      // Add new reaction
      reaction = {
        emoji,
        uid: userData.uid,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        reactedOn: new Date(),
      };
      updatedReactions = [...reactions, reaction];
      isAdding = true;
    }

    await messageRef.set(
      {
        reactions: updatedReactions,
      },
      { merge: true }
    );

    consola.info("+++++ FIRESTORE SUCCESS => MessageReactions: toggleMessageReaction", {
      isAdding,
      reactionsCount: updatedReactions.length,
    });

    return { 
      success: true, 
      reaction,
      removedReaction 
    };
  } catch (error) {
    consola.error("+++++ FIRESTORE ERROR => MessageReactions: toggleMessageReaction", error);
    throw error;
  }
};

/**
 * Send a reply message to a group
 */
export const sendReplyMessage = async (
  groupId: string,
  messageText: string,
  replyToMessage: any
): Promise<ReplyResponse> => {
  try {
    const organizationId = getRecoil(organizationIdSelector);
    const userData = getRecoil(userDataState) as any;

    if (!organizationId || !userData) {
      throw new Error('Missing organization ID or user data');
    }

    if (!groupId || !messageText || !replyToMessage) {
      throw new Error('Missing required data in sendReplyMessage');
    }

    consola.info("+++++ FIRESTORE => MessageReactions: sendReplyMessage", {
      groupId,
      messageText: messageText.substring(0, 50) + '...',
      replyToMessageId: replyToMessage.id,
    });

    const messageId = nanoid();
    const batch = firebase.firestore().batch();

    // Create reply-to reference
    const replyTo: ReplyToMessage = {
      messageText: replyToMessage.message,
      messageType: replyToMessage.messageType,
      fromUser: {
        firstName: replyToMessage.fromUser.firstName,
        lastName: replyToMessage.fromUser.lastName,
        uid: replyToMessage.fromUID,
      },
      messageId: replyToMessage.id,
      createdOn: replyToMessage.createdOn,
    };

    // Only include media URLs if they exist (avoid undefined values)
    if (replyToMessage.imageUrl) {
      replyTo.imageUrl = replyToMessage.imageUrl;
    }
    if (replyToMessage.videoUrl) {
      replyTo.videoUrl = replyToMessage.videoUrl;
    }
    if (replyToMessage.audioUrl) {
      replyTo.audioUrl = replyToMessage.audioUrl;
    }
    if (replyToMessage.fileUrl) {
      replyTo.fileUrl = replyToMessage.fileUrl;
    }
    if (replyToMessage.pdfUrl) {
      replyTo.pdfUrl = replyToMessage.pdfUrl;
    }

    // Create the reply message
    const messageRef = firebase
      .firestore()
      .collection('organizations')
      .doc(organizationId)
      .collection('messageGroups')
      .doc(groupId)
      .collection('messages')
      .doc(messageId);

    batch.set(
      messageRef,
      {
        id: messageId,
        createdOn: new Date(),
        fromUID: userData.uid,
        groupId: groupId,
        fromUser: userData,
        messageType: 'reply',
        processed: false,
        processedOn: null,
        viewedBy: [],
        appDestination: 'messaging',
        message: messageText,
        replyTo: replyTo,
      },
      { merge: true }
    );

    // Update the group with the last message
    const groupRef = firebase
      .firestore()
      .collection('organizations')
      .doc(organizationId)
      .collection('messageGroups')
      .doc(groupId);

    batch.update(groupRef, {
      lastMessage: messageText,
      lastUser: userData,
      lastDate: new Date(),
    });

    await batch.commit();

    consola.info("+++++ FIRESTORE SUCCESS => MessageReactions: sendReplyMessage", {
      messageId,
    });

    return {
      success: true,
      messageId,
    };
  } catch (error) {
    consola.error("+++++ FIRESTORE ERROR => MessageReactions: sendReplyMessage", error);
    throw error;
  }
};
